import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { MessageSquareHeart } from "lucide-react";
import { toast } from "sonner";

import { SiteFooter } from "@/components/site-footer";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { submitFeedback } from "@/lib/feedback.functions";

export const Route = createFileRoute("/feedback")({
  head: () => ({
    meta: [
      { title: "Feedback — Understand Your Genome" },
      {
        name: "description",
        content:
          "Send a suggestion, bug report, or idea to the people building Understand Your Genome — no email client needed.",
      },
      { property: "og:title", content: "Feedback — Understand Your Genome" },
      {
        property: "og:description",
        content: "Tell us what's working, what's broken, or what you'd like to see.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: FeedbackPage,
});

function FeedbackPage() {
  const send = useServerFn(submitFeedback);
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;
    if (!message.trim()) {
      toast.error("Please write a message first.");
      return;
    }
    setBusy(true);
    try {
      await send({ data: { message: message.trim(), email: email.trim() || undefined } });
      setSent(true);
      toast.success("Thank you — your feedback is on its way.");
    } catch {
      toast.error("We couldn't send that just now. Please try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SiteHeader />
      <main className="mx-auto max-w-2xl px-5 py-16">
      <div className="flex items-center gap-3 text-primary">
        <MessageSquareHeart className="size-7" />
        <h1 className="font-display text-3xl text-foreground">Feedback</h1>
      </div>
      <p className="mt-3 leading-relaxed text-muted-foreground">
        Found a bug, have an idea, or wish something worked differently? Tell us here — it goes
        straight to the people building this.
      </p>

      {sent ? (
        <div className="mt-8 rounded-2xl border border-border bg-card p-6">
          <h2 className="font-display text-xl">Thank you.</h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Your message has been received. We read everything, even if we can't reply to each
            note individually.
          </p>
          <Button
            variant="outline"
            className="mt-5"
            onClick={() => {
              setSent(false);
              setMessage("");
              setEmail("");
            }}
          >
            Send another message
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mt-8 space-y-5 rounded-2xl border border-border bg-card p-6">
          <div className="space-y-2">
            <Label htmlFor="feedback-message">What's on your mind? Bugs, ideas, anything.</Label>
            <Textarea
              id="feedback-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              maxLength={5000}
              placeholder="For example: the search on my results page didn't understand…"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="feedback-email">
              Email <span className="text-muted-foreground">(optional — only if you'd like a reply)</span>
            </Label>
            <Input
              id="feedback-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={255}
              placeholder="you@example.org"
            />
          </div>
          <Button type="submit" disabled={busy}>
            {busy ? "Sending…" : "Send feedback"}
          </Button>
        </form>
      )}
      </main>
      <SiteFooter />
    </div>
  );
}
